import { useState, useEffect } from 'react';
import axios from 'axios';
import * as LucideIcons from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { EditableText } from '../components/EditableText';
import { services } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Built-in fallback images used when the Super Admin hasn't uploaded any
// Services-page images yet, so the page still looks complete out of the box.
const FALLBACK_SERVICE_IMAGES = [
  'https://images.unsplash.com/photo-1666871701015-6fab56ecd4c8?crop=entropy&cs=srgb&fm=jpg&q=85',
  'https://images.unsplash.com/photo-1633536704679-de310869515b?crop=entropy&cs=srgb&fm=jpg&q=85',
  'https://images.unsplash.com/photo-1732896065182-ad7d8fdc766f?crop=entropy&cs=srgb&fm=jpg&q=85',
  'https://images.unsplash.com/photo-1772291899685-0b33cb04df45?crop=entropy&cs=srgb&fm=jpg&q=85',
  'https://images.unsplash.com/photo-1762604462465-76720d039e9f?crop=entropy&cs=srgb&fm=jpg&q=85',
  'https://images.unsplash.com/photo-1726499180628-7b0975170df8?crop=entropy&cs=srgb&fm=jpg&q=85',
];

export default function Services() {
  // Map of `slug → image url` populated from the admin-managed
  // /api/service-images endpoint. Missing slugs fall back to the
  // bundled stock image for that card so the page is never blank.
  const [customBySlug, setCustomBySlug] = useState({});

  useEffect(() => {
    axios
      .get(`${API}/service-images/public`)
      .then((r) => {
        const map = {};
        (Array.isArray(r.data) ? r.data : []).forEach((row) => {
          if (row.slug && row.url) map[row.slug] = row.url;
        });
        setCustomBySlug(map);
      })
      .catch(() => setCustomBySlug({}));
  }, []);

  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon className="w-8 h-8" /> : null;
  };

  // Per-card image lookup: a custom upload always wins, otherwise we
  // fall back to the curated stock image at the same index.
  const imageFor = (service, index) =>
    customBySlug[service.slug] || FALLBACK_SERVICE_IMAGES[index] || FALLBACK_SERVICE_IMAGES[0];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-[#193460] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">
            <EditableText contentKey="services.header.title" as="span">Our Repair Services</EditableText>
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            <EditableText contentKey="services.header.subtitle" as="span" multiline>
              Our skilled volunteers are here to help repair a wide variety of household items. All services are completely free!
            </EditableText>
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={service.id} className="border-2 border-gray-200 hover:border-[#E87B6A] transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2 overflow-hidden group">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={imageFor(service, index)}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-[#E87B6A] rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#193460] transition-colors duration-300">
                      {getIcon(service.icon)}
                    </div>
                    <CardTitle className="text-xl text-[#193460]">
                      <EditableText contentKey={`services.card.${service.id}.title`} as="span">{service.title}</EditableText>
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">
                    <EditableText contentKey={`services.card.${service.id}.description`} as="span" multiline>{service.description}</EditableText>
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-[#E87B6A] shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#E87B6A] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <LucideIcons.Info className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#193460] mb-4">
                    <EditableText contentKey="services.notice.title" as="span">Important Information</EditableText>
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    <EditableText contentKey="services.notice.intro" as="span" multiline>
                      While we strive to repair as many items as possible, please note that all repairs depend on:
                    </EditableText>
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <LucideIcons.CheckCircle2 className="w-5 h-5 text-[#E87B6A] mr-2 mt-0.5 flex-shrink-0" />
                      <span><EditableText contentKey="services.notice.bullet1" as="span">Volunteer expertise and skills available on the day</EditableText></span>
                    </li>
                    <li className="flex items-start">
                      <LucideIcons.CheckCircle2 className="w-5 h-5 text-[#E87B6A] mr-2 mt-0.5 flex-shrink-0" />
                      <span><EditableText contentKey="services.notice.bullet2" as="span">Availability of necessary parts and tools</EditableText></span>
                    </li>
                    <li className="flex items-start">
                      <LucideIcons.CheckCircle2 className="w-5 h-5 text-[#E87B6A] mr-2 mt-0.5 flex-shrink-0" />
                      <span><EditableText contentKey="services.notice.bullet3" as="span">The nature and complexity of the repair required</EditableText></span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    <EditableText contentKey="services.notice.outro" as="span" multiline>
                      We cannot guarantee that every item can be fixed, but our volunteers will do their best to help!
                    </EditableText>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#193460] mb-6">
            <EditableText contentKey="services.cta.title" as="span">Need Something Repaired?</EditableText>
          </h2>
          <p className="text-xl text-gray-700 mb-8 leading-relaxed">
            <EditableText contentKey="services.cta.subtitle" as="span" multiline>
              Submit a repair request and bring your item to our next monthly event.
            </EditableText>
          </p>
          <a href="/repair-request">
            <button className="bg-[#E87B6A] hover:bg-[#d66a59] text-white font-semibold px-10 py-4 rounded-lg text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              Submit Repair Request
              <LucideIcons.ArrowRight className="inline-block ml-2 w-5 h-5" />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
}
